import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiFetch from './api';
import { parseTripsCSV, eventsToCSV, downloadCSV } from './csvUtils';
import './css/EventGeneratorForm.css';

// Default values applied by the backend when a field is left empty.
// They are pre-filled into the form as editable values so users can see and change them directly.
const EVENT_FIELD_DEFAULTS = {
    seqNumber: '1',
    tempCelsius: '25',
    latitude: '33.606198',
    longitude: '-117.837003',
    speed: '50',
    odometer: '1',
    mainMillivolts: '12000',
    battMillivolts: '7500',
    auxMillivolts: '12000',
    solarMillivolts: '12000'
};

// Default values for the "Create Own Trip" event fields, mirroring the values applied when left empty.
const TRIP_EVENT_FIELD_DEFAULTS = {
    latitude: '33.606198',
    longitude: '-117.837003',
    altitude: '30',
    speed: '50',
    heading: '37',
    odometer: '50',
    satellites: '12',
    hdop: '.6',
    rssi: '30',
    sequence: '1',
    distance: '10',
    voltage: '12000'
};

const DEFAULT_TRIP_EVENT_DELAY_SECONDS = '10';

// All environments the form can target. Access to each one is gated by the
// logged-in user's role (see deriveAllowedEnvironments below).
const ALL_ENVIRONMENTS = [
    { value: 'Staging', text: 'Staging' },
    { value: 'Production', text: 'Production' }
];

const DTC_EVENT_CODES = [
    { code: 'P0405', description: 'EGR Sensor A Circuit Low' },
    { code: 'P2196', description: 'O2 Sensor Signal Biased/Stuck Rich Bank 1 Sensor 1' },
    { code: 'P2198', description: 'O2 Sensor Signal Biased/Stuck Rich Bank 2 Sensor 1' },
    { code: 'P009E', description: 'Fuel Pressure Relief Control Performance/Stuck Off' },
    { code: 'P0456', description: 'EVAP System Leak Detected (very small leak)' },
    { code: 'P0121', description: 'Throttle/Pedal Position Sensor/Switch A Circuit Range/Performance' },
    { code: 'P0333', description: 'Knock/Combustion Vibration Sensor 2 Circuit High Bank 2' },
    { code: 'P0442', description: 'EVAP System Leak Detected (small leak)' },
    { code: 'P0455', description: 'EVAP System Leak Detected (large leak)' },
    { code: 'P0457', description: 'EVAP System Leak Detected (fuel cap loose/off)' },
    { code: 'P0440', description: 'EVAP System' },
    { code: 'P0441', description: 'EVAP System Incorrect Purge Flow' },
    { code: 'P20BE', description: 'Reductant Heater B Control Circuit Performance' },
    { code: 'P0420', description: 'Catalyst System Efficiency Below Threshold Bank 1' },
    { code: 'P0430', description: 'Catalyst System Efficiency Below Threshold Bank 2' },
    { code: 'P0449', description: 'EVAP System Vent Valve Control Circuit/Open' },
    { code: 'P0650', description: 'MIL Control Circuit/Open' },
    { code: 'P0132', description: 'O2 Sensor Circuit High Voltage Bank 1 Sensor 1' },
    { code: 'P0133', description: 'O2 Sensor Circuit Slow Response Bank 1 Sensor 1' },
    { code: 'P0134', description: 'O2 Sensor Circuit No Activity Detected Bank 1 Sensor 1' },
    { code: 'P0137', description: 'O2 Sensor Circuit Low Voltage Bank 1 Sensor 2' },
    { code: 'P0172', description: 'System Too Rich Bank 1' },
    { code: 'P0300', description: 'Random/Multiple Cylinder Misfire Detected' },
    { code: 'P0496', description: 'EVAP System High Purge Flow' },
    { code: 'P20EE', description: 'SCR NOx Catalyst Efficiency Below Threshold Bank 1' },
    { code: 'P0138', description: 'O2 Sensor Circuit High Voltage Bank 1 Sensor 2' },
    { code: 'P013E', description: 'O2 Sensor Delayed Response - Rich to Lean Bank 1 Sensor 2' },
    { code: 'P0158', description: 'O2 Sensor Circuit High Voltage Bank 2 Sensor 2' },
    { code: 'P2096', description: 'Post Catalyst Fuel Trim System Too Lean Bank 1' },
    { code: 'P2098', description: 'Post Catalyst Fuel Trim System Too Lean Bank 2' },
    { code: 'P2273', description: 'O2 Sensor Signal Biased/Stuck Rich Bank 2 Sensor 2' },
    { code: 'P061B', description: 'Internal Control Module Torque Calculation Performance' },
    { code: 'P2459', description: 'Particulate Filter Regeneration Frequency Bank 1' },
    { code: 'P203F', description: 'Reductant Level Too Low' },
    { code: 'P20E8', description: 'Reductant Pressure Too Low' },
    { code: 'P0523', description: 'Engine Oil Pressure Sensor/Switch A High' },
    { code: 'P0150', description: 'O2 Sensor Circuit Bank 2 Sensor 1' },
    { code: 'P0571', description: 'Brake Switch A Circuit' },
    { code: 'P0101', description: 'Mass or Volume Air Flow Sensor A Circuit Range/Performance' },
    { code: 'P0128', description: 'Coolant Thermostat (Coolant Temperature Below Thermostat Regulating Temperature)' },
    { code: 'P0443', description: 'EVAP System Purge Control Valve A Circuit' },
    { code: 'P0050', description: 'HO2S Heater Control Circuit Bank 2 Sensor 1' },
    { code: 'P051B', description: 'Crankcase Pressure Sensor A Circuit Range/Performance' },
    { code: 'P0308', description: 'Cylinder 8 Misfire Detected' },
    { code: 'P2135', description: 'Throttle/Pedal Position Sensor/Switch A/B Voltage Correlation' },
    { code: 'P015B', description: 'O2 Sensor Delayed Response - Lean to Rich Bank 1 Sensor 1' },
    { code: 'P0171', description: 'System Too Lean Bank 1' },
    { code: 'P2097', description: 'Post Catalyst Fuel Trim System Too Rich Bank 1' },
    { code: 'P219A', description: 'Bank 1 Air-Fuel Ratio Imbalance' },
    { code: 'P2270', description: 'O2 Sensor Signal Biased/Stuck Lean Bank 1 Sensor 2' },
    { code: 'P0404', description: 'EGR A Control Circuit Range/Performance' },
    { code: 'P0401', description: 'EGR A Flow Insufficient Detected' },
    { code: 'P2099', description: 'Post Catalyst Fuel Trim System Too Rich Bank 2' },
    { code: 'P0059', description: 'HO2S Heater Resistance Bank 2 Sensor 1' },
    { code: 'P0154', description: 'O2 Sensor Circuit No Activity Detected Bank 2 Sensor 1' }
];

// Inspect the authentication payload (roles/authorities returned by
// /api/auth/status) and work out which environments the user may target.
// Staging users can only send to Staging; production users only to Production.
// A user carrying both roles (e.g. an admin) keeps access to both.
const deriveAllowedEnvironments = (authData) => {
    if (!authData) {
        return [];
    }

    // Collect every role/authority-like string the backend might expose.
    const rawValues = []
        .concat(authData.roles || [])
        .concat(authData.authorities || [])
        .concat(authData.role ? [authData.role] : [])
        .concat(authData.environment ? [authData.environment] : [])
        .concat(authData.allowedEnvironments || [])
        .map(v => (typeof v === 'string' ? v : (v && (v.authority || v.role || v.name)) || ''))
        .map(v => String(v).toLowerCase());

    const haystack = rawValues.join(' ');
    const allowsStaging = haystack.includes('stag');
    const allowsProduction = haystack.includes('prod');

    const allowed = [];
    if (allowsStaging) allowed.push('Staging');
    if (allowsProduction) allowed.push('Production');
    return allowed;
};

const EventGeneratorForm = () => {
    const navigate = useNavigate();

    // Environments the current user is authorized to target, derived from their
    // role. Empty array = not yet loaded / no restriction resolved.
    const [allowedEnvironments, setAllowedEnvironments] = useState([]);

    // Form state
    const [formData, setFormData] = useState({
        environment: '',
        serialNumber: '',
        isTrip: '',
        deviceType: '',
        tripName: '',
        deviceCommandType: '',
        commandStatus: '',
        csvFileName: null,
        eventName: '',
        dtcCode1: '',
        dtcCode2: '',
        dtcCode3: '',
        commitId: '',
        seqNumber: EVENT_FIELD_DEFAULTS.seqNumber,
        tempCelsius: EVENT_FIELD_DEFAULTS.tempCelsius,
        latitude: EVENT_FIELD_DEFAULTS.latitude,
        longitude: EVENT_FIELD_DEFAULTS.longitude,
        speed: EVENT_FIELD_DEFAULTS.speed,
        odometer: EVENT_FIELD_DEFAULTS.odometer,
        mainMillivolts: EVENT_FIELD_DEFAULTS.mainMillivolts,
        battMillivolts: EVENT_FIELD_DEFAULTS.battMillivolts,
        auxMillivolts: EVENT_FIELD_DEFAULTS.auxMillivolts,
        solarMillivolts: EVENT_FIELD_DEFAULTS.solarMillivolts,
        cargoLoaded: 'true',
        previousDateTime: '',
        waitTime: '',
        deviceId: '',
        deviceCommandValue: '',
        commandMode: '',
        commandDuration: '',
        carrierStatus: 'Enable',
        networkProvider: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    // Default values pre-filled into the optional Event fields (editable).
    const eventFieldDefaults = EVENT_FIELD_DEFAULTS;

    // Default values pre-filled into the "Create Own Trip" event fields (editable).
    const tripEventFieldDefaults = TRIP_EVENT_FIELD_DEFAULTS;
    // Device type auto-detection (populated from the serial number)
    const [deviceTypeLookup, setDeviceTypeLookup] = useState({ loading: false, error: '' });
    // True when the device type was auto-populated from the serial number, which
    // makes the device type dropdown read-only/uneditable.
    const [deviceTypeAutoDetected, setDeviceTypeAutoDetected] = useState(false);
    // Monotonically increasing id used to ignore stale/out-of-order lookup responses
    const deviceLookupSeqRef = useRef(0);

    // Trip events state for customTrip
    const [tripEvents, setTripEvents] = useState([]);
    const [csvImportErrors, setCsvImportErrors] = useState([]);
    const [csvImportSuccess, setCsvImportSuccess] = useState('');
    const [releaseTripRun, setReleaseTripRun] = useState(null);
    const releaseTripStopRef = useRef(false);
    const releaseTripWaitRef = useRef(null);
    const isReleaseTripActive = ['loading', 'running', 'waiting', 'stopping'].includes(releaseTripRun?.status);
    const hasCustomTripEventDelays = tripEvents.some(event => event.delay !== '' && event.delay != null);

    useEffect(() => {
        if (releaseTripRun?.status !== 'waiting' || !releaseTripRun.nextSendAt) return undefined;

        const nextSendAt = releaseTripRun.nextSendAt;
        const updateRemainingTime = () => {
            const remainingMs = Math.max(0, nextSendAt - Date.now());
            setReleaseTripRun(prev => prev?.nextSendAt === nextSendAt
                ? { ...prev, remainingMs }
                : prev
            );
        };

        updateRemainingTime();
        const timerId = setInterval(updateRemainingTime, 100);
        return () => clearInterval(timerId);
    }, [releaseTripRun?.nextSendAt, releaseTripRun?.status]);

    // Fetch the logged-in user's role and restrict the environment options
    // accordingly. Staging users may only target Staging; production users only
    // Production. When exactly one environment is allowed, pre-select it.
    useEffect(() => {
        let isMounted = true;
        apiFetch('/api/auth/status', { headers: { Accept: 'application/json' } })
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                if (!isMounted || !data) return;
                const allowed = deriveAllowedEnvironments(data);
                setAllowedEnvironments(allowed);
                if (allowed.length === 1) {
                    setFormData(prev => ({ ...prev, environment: allowed[0] }));
                }
            })
            .catch(() => {
                // On failure leave the options unrestricted so the form stays usable;
                // the backend still enforces access control on submit.
            });
        return () => { isMounted = false; };
    }, []);

    // The environment options actually shown to the user. If the role couldn't
    // be resolved we fall back to all environments (backend remains the source
    // of truth for authorization).
    const environmentOptions = allowedEnvironments.length > 0
        ? ALL_ENVIRONMENTS.filter(opt => allowedEnvironments.includes(opt.value))
        : ALL_ENVIRONMENTS;

    // Device-specific event type options for customTrip
    const deviceEventTypes = {
        XirgoHcf: [
            { value: "speedUpdate", text: "Speed Update" },
            { value: "heartbeat", text: "Heartbeat" },
            { value: "default", text: "Default" },
            { value: "oneWire", text: "One Wire" },
            { value: "motion", text: "Motion" },
            { value: "accelAlert", text: "Accel Alert" },
            { value: "decelAlert", text: "Decel Alert" },
            { value: "rpmAlert", text: "RPM Alert" },
            { value: "moveStart", text: "Move Start" },
            { value: "movement", text: "Movement" },
            { value: "stopped", text: "Stopped" },
            { value: "dtc", text: "DTC" },
            { value: "adminLocate", text: "Admin Locate" },
            { value: "userLocate", text: "User Locate" },
            { value: "idleReport", text: "Idle Report" },
            { value: "unauthMovement", text: "Unauth Movement" },
            { value: "sleepStart", text: "Sleep Start" },
            { value: "sleepStop", text: "Sleep Stop" },
            { value: "starterDisabled", text: "Starter Disabled" },
            { value: "starterEnabled", text: "Starter Enabled" },
            { value: "doorUnlock", text: "Door Unlock" },
            { value: "startTow", text: "Start Tow" },
            { value: "doorLock", text: "Door Lock" },
            { value: "idle", text: "Idle" },
            { value: "ignOff", text: "Ignition Off" },
            { value: "ignOn", text: "Ignition On" },
            { value: "input3Low", text: "Input 3 Low" },
            { value: "input3High", text: "Input 3 High" },
            { value: "excessiveSpeed", text: "Excessive Speed" },
            { value: "serviceMileage", text: "Service Mileage" },
            { value: "serviceHours", text: "Service Hours" },
            { value: "input2Low", text: "Input 2 Low" },
            { value: "input2High", text: "Input 2 High" },
            { value: "maintenance", text: "Maintenance" },
            { value: "lowBattery", text: "Low Battery" },
            { value: "powerDisconnect", text: "Power Disconnect" },
            { value: "powerReconnect", text: "Power Reconnect" },
            { value: "extremeAccel", text: "Extreme Accel" },
            { value: "extremeDecel", text: "Extreme Decel" },
            { value: "input1Low", text: "Input 1 Low" },
            { value: "input1High", text: "Input 1 High" },
            { value: "powerUpHardReset", text: "Power Up Hard Reset" },
            { value: "enterSleep", text: "Enter Sleep" },
            { value: "wakeUpVoltage", text: "Wake Up Voltage" },
            { value: "virtIgnOnVolt", text: "Virt Ign On Volt" },
            { value: "virtIgnOffVolt", text: "Virt Ign Off Volt" },
            { value: "hibernating", text: "Hibernating" },
            { value: "scriptUpdate", text: "Script Update" },
            { value: "hibernateRequest", text: "Hibernate Request" },
            { value: "collision", text: "Collision" },
            { value: "wakeUpNetwork", text: "Wake Up Network" },
            { value: "userMessage", text: "User Message" },
            { value: "appMessage", text: "App Message" },
            { value: "paramMessage", text: "Param Message" },
            { value: "ipAddrChange", text: "IP Addr Change" },
            { value: "ptoCleared", text: "PTO Cleared" },
            { value: "ptoAsserted", text: "PTO Asserted" }
        ],
        OnceEvent: [
            { value: "default", text: "Default" },
            { value: "powerConnect", text: "Power Connect" },
            { value: "heartbeat", text: "Heartbeat" },
            { value: "battHeartbeat", text: "Batt Heartbeat" },
            { value: "batteryPower", text: "Battery Power" },
            { value: "externalPower", text: "External Power" },
            { value: "lowExternalPower", text: "Low External Power" },
            { value: "normalExternalPower", text: "Normal External Power" },
            { value: "battLow", text: "Batt Low" },
            { value: "battNormal", text: "Batt Normal" },
            { value: "enterSleep", text: "Enter Sleep" },
            { value: "exitSleep", text: "Exit Sleep" },
            { value: "starterDisable", text: "Starter Disable" },
            { value: "starterEnable", text: "Starter Enable" },
            { value: "ignitionOn", text: "Ignition On" },
            { value: "ignitionOff", text: "Ignition Off" },
            { value: "gpsLock", text: "GPS Lock" },
            { value: "gpsUnlock", text: "GPS Unlock" },
            { value: "networkChange", text: "Network Change" },
            { value: "speedingStart", text: "Speeding Start" },
            { value: "speedingStop", text: "Speeding Stop" },
            { value: "tripStart", text: "Trip Start" },
            { value: "tripStop", text: "Trip Stop" },
            { value: "movement", text: "Movement" },
            { value: "stopped", text: "Stopped" },
            { value: "headingChange", text: "Heading Change" },
            { value: "driveDistance", text: "Drive Distance" },
            { value: "towed", text: "Towed" },
            { value: "relayOverride", text: "Relay Override" },
            { value: "idle", text: "Idle" },
            { value: "userlocate", text: "User Locate" },
            { value: "reset", text: "Reset" },
            { value: "odometerInterval", text: "Odometer Interval" },
            { value: "maintenance", text: "Maintenance" },
            { value: "extendedStop", text: "Extended Stop" },
            { value: "extendedStop2", text: "Extended Stop 2" },
            { value: "firmwareFailed", text: "Firmware Failed" },
            { value: "firmwareSuccess", text: "Firmware Success" },
            { value: "vinChange", text: "VIN Change" },
            { value: "dtcDetected", text: "DTC Detected" },
            { value: "gMotion", text: "G Motion" },
            { value: "criticalExternalPower", text: "Critical External Power" },
            { value: "unauth", text: "Unauth" },
            { value: "highExternalPower", text: "High External Power" },
            { value: "enteredGeofence", text: "Entered Geofence" },
            { value: "exitedGeofence", text: "Exited Geofence" },
            { value: "tempHigh", text: "Temp High" },
            { value: "tempNormal", text: "Temp Normal" },
            { value: "tempLow", text: "Temp Low" },
            { value: "tamper", text: "Tamper" },
            { value: "cargoSensor", text: "Cargo Sensor" },
            { value: "cellLock", text: "Cell Lock" },
            { value: "cellUnlock", text: "Cell Unlock" }
        ],
        MontageIon: [
            { value: "default", text: "Default" },
            { value: "heartbeat", text: "Heartbeat" },
            { value: "powerConnect", text: "Power Connect" },
            { value: "speedingStart", text: "Speeding Start" },
            { value: "gp1Trans", text: "GP1 Trans" },
            { value: "gp2Trans", text: "GP2 Trans" },
            { value: "starterEnable", text: "Starter Enable" },
            { value: "lowExternalPower", text: "Low External Power" },
            { value: "driveDistance", text: "Drive Distance" },
            { value: "gpsLock", text: "GPS Lock" },
            { value: "gpsUnlock", text: "GPS Unlock" },
            { value: "lowPower", text: "Low Power" },
            { value: "externalPower", text: "External Power" },
            { value: "userlocate", text: "User Locate" },
            { value: "idle", text: "Idle" },
            { value: "ignitionOn", text: "Ignition On" },
            { value: "ignitionOff", text: "Ignition Off" },
            { value: "towed", text: "Towed" },
            { value: "headingChange", text: "Heading Change" },
            { value: "batteryPower", text: "Battery Power" },
            { value: "reset", text: "Reset" },
            { value: "movement", text: "Movement" },
            { value: "stopped", text: "Stopped" },
            { value: "networkChange", text: "Network Change" },
            { value: "extendedStop", text: "Extended Stop" },
            { value: "odometerInterval", text: "Odometer Interval" },
            { value: "firmwareFailed", text: "Firmware Failed" },
            { value: "extendedStop2", text: "Extended Stop 2" },
            { value: "enteredGeofence", text: "Entered Geofence" },
            { value: "exitedGeofence", text: "Exited Geofence" },
            { value: "harshAccel", text: "Harsh Accel" },
            { value: "harshBrake", text: "Harsh Brake" },
            { value: "harshCorner", text: "Harsh Corner" },
            { value: "iioMode", text: "IIO Mode" },
            { value: "tftpOk", text: "TFTP Ok" },
            { value: "powerNormal", text: "Power Normal" },
            { value: "powerHigh", text: "Power High" },
            { value: "homeNetwork", text: "Home Network" },
            { value: "roamingNetwork", text: "Roaming Network" }
        ],
        Queclink: [
            { value: "geofenceExit", text: "Geofence Exit" },
            { value: "heartbeat", text: "Heartbeat" },
            { value: "externalPowerOn", text: "External Power On" },
            { value: "stopped", text: "Stopped" },
            { value: "userLocate", text: "User Locate" },
            { value: "reset", text: "Reset" }
        ],
        MontageArsenal: [
            { value: "default", text: "Default" },
            { value: "powerConnect", text: "Power Connect" },
            { value: "heartbeat", text: "Heartbeat" },
            { value: "battHeartbeat", text: "Batt Heartbeat" },
            { value: "batteryPower", text: "Battery Power" },
            { value: "externalPower", text: "External Power" },
            { value: "lowExternalPower", text: "Low External Power" },
            { value: "normalExternalPower", text: "Normal External Power" },
            { value: "battLow", text: "Batt Low" },
            { value: "battNormal", text: "Batt Normal" },
            { value: "enterSleep", text: "Enter Sleep" },
            { value: "exitSleep", text: "Exit Sleep" },
            { value: "starterDisable", text: "Starter Disable" },
            { value: "starterEnable", text: "Starter Enable" },
            { value: "ignitionOn", text: "Ignition On" },
            { value: "ignitionOff", text: "Ignition Off" },
            { value: "gpsLock", text: "GPS Lock" },
            { value: "gpsUnlock", text: "GPS Unlock" },
            { value: "networkChange", text: "Network Change" },
            { value: "speedingStart", text: "Speeding Start" },
            { value: "speedingStop", text: "Speeding Stop" },
            { value: "tripStart", text: "Trip Start" },
            { value: "tripStop", text: "Trip Stop" },
            { value: "movement", text: "Movement" },
            { value: "stopped", text: "Stopped" },
            { value: "headingChange", text: "Heading Change" },
            { value: "driveDistance", text: "Drive Distance" },
            { value: "towed", text: "Towed" },
            { value: "relayOverride", text: "Relay Override" },
            { value: "idle", text: "Idle" },
            { value: "userlocate", text: "User Locate" },
            { value: "reset", text: "Reset" },
            { value: "odometerInterval", text: "Odometer Interval" },
            { value: "maintenance", text: "Maintenance" },
            { value: "extendedStop", text: "Extended Stop" },
            { value: "extendedStop2", text: "Extended Stop 2" },
            { value: "firmwareFailed", text: "Firmware Failed" },
            { value: "firmwareSuccess", text: "Firmware Success" },
            { value: "vinChange", text: "VIN Change" },
            { value: "dtcDetected", text: "DTC Detected" },
            { value: "gMotion", text: "G Motion" },
            { value: "criticalExternalPower", text: "Critical External Power" },
            { value: "unauth", text: "Unauth" },
            { value: "highExternalPower", text: "High External Power" },
            { value: "enteredGeofence", text: "Entered Geofence" },
            { value: "exitedGeofence", text: "Exited Geofence" },
            { value: "tempHigh", text: "Temp High" },
            { value: "tempNormal", text: "Temp Normal" },
            { value: "tempLow", text: "Temp Low" },
            { value: "tamper", text: "Tamper" },
            { value: "cargoSensor", text: "Cargo Sensor" },
            { value: "cellLock", text: "Cell Lock" },
            { value: "cellUnlock", text: "Cell Unlock" }
        ],
        ApolloUdp: [
            { value: "default", text: "Default" },
            { value: "tripStart", text: "Trip Start" },
            { value: "tripStop", text: "Trip Stop" },
            { value: "movement", text: "Movement" },
            { value: "driveDistance", text: "Drive Distance" },
            { value: "enteredGeofence", text: "Entered Geofence" },
            { value: "exitedGeofence", text: "Exited Geofence" },
            { value: "speedingStart", text: "Speeding Start" },
            { value: "towed", text: "Towed" },
            { value: "idle", text: "Idle" },
            { value: "unpowered", text: "Unpowered" },
            { value: "extendedStop", text: "Extended Stop" },
            { value: "abandoned", text: "Abandoned" },
            { value: "harshAccel", text: "Harsh Accel" },
            { value: "headingChange", text: "Heading Change" },
            { value: "speedingStop", text: "Speeding Stop" },
            { value: "stopped", text: "Stopped" },
            { value: "towStop", text: "Tow Stop" },
            { value: "powerConnect", text: "Power Connect" },
            { value: "batteryPower", text: "Battery Power" },
            { value: "highExternalPower", text: "High External Power" },
            { value: "lowExternalPower", text: "Low External Power" },
            { value: "criticalExternalPower", text: "Critical External Power" },
            { value: "normalExternalPower", text: "Normal External Power" },
            { value: "highInternalBatteryPower", text: "High Internal Battery Power" },
            { value: "lowInternalBatteryPower", text: "Low Internal Battery Power" },
            { value: "criticalInternalBatteryPower", text: "Critical Internal Battery Power" },
            { value: "normalInternalBatteryPower", text: "Normal Internal Battery Power" },
            { value: "batteryCharging", text: "Battery Charging" },
            { value: "powerReadyToXmit", text: "Power Ready To Xmit" },
            { value: "powerMonitoring", text: "Power Monitoring" },
            { value: "powerStandby", text: "Power Standby" },
            { value: "powerHibernate", text: "Power Hibernate" },
            { value: "powerChargingStopped", text: "Power Charging Stopped" },
            { value: "powerAuxPowerConnected", text: "Power Aux Power Connected" },
            { value: "solarConnected", text: "Solar Connected" },
            { value: "externalPowerDisconnect", text: "External Power Disconnect" },
            { value: "auxPowerDisconnect", text: "Aux Power Disconnect" },
            { value: "solarDisconnect", text: "Solar Disconnect" },
            { value: "internalBatteryNotInUse", text: "Internal Battery Not In Use" },
            { value: "internalBatteryConnected", text: "Internal Battery Connected" },
            { value: "internalBatteryDisconnected", text: "Internal Battery Disconnected" },
            { value: "gpioStateChange", text: "GPIO State Change" },
            { value: "ignitionOn", text: "Ignition On" },
            { value: "ignitionOff", text: "Ignition Off" },
            { value: "starterEnable", text: "Starter Enable" },
            { value: "starterDisable", text: "Starter Disable" },
            { value: "tamper", text: "Tamper" },
            { value: "gpioHigh", text: "GPIO High" },
            { value: "gpioLow", text: "GPIO Low" },
            { value: "doorOpen", text: "Door Open" },
            { value: "doorClosed", text: "Door Closed" },
            { value: "cargoState", text: "Cargo State" },
            { value: "temperature", text: "Temperature" },
            { value: "highTemperature", text: "High Temperature" },
            { value: "lowTemperature", text: "Low Temperature" },
            { value: "normalTemperature", text: "Normal Temperature" },
            { value: "cargoVolume", text: "Cargo Volume" },
            { value: "cargoDoor", text: "Cargo Door" },
            { value: "tilt", text: "Tilt" },
            { value: "lowTire", text: "Low Tire" },
            { value: "normalTire", text: "Normal Tire" },
            { value: "dtcDetected", text: "DTC Detected" },
            { value: "checkEngine", text: "Check Engine" },
            { value: "lowCharge", text: "Low Charge" },
            { value: "odometer", text: "Odometer" },
            { value: "powerUp", text: "Power Up" },
            { value: "heartbeat", text: "Heartbeat" },
            { value: "maintenanceRequest", text: "Maintenance Request" },
            { value: "fota", text: "FOTA" },
            { value: "userlocate", text: "User Locate" },
            { value: "reset", text: "Reset" },
            { value: "enterSleep", text: "Enter Sleep" },
            { value: "cellLock", text: "Cell Lock" },
            { value: "cellUnlock", text: "Cell Unlock" },
            { value: "gpsUnlock", text: "GPS Unlock" },
            { value: "gpsLock", text: "GPS Lock" },
            { value: "ipv4Change", text: "IPv4 Change" },
            { value: "ipv6Change", text: "IPv6 Change" },
            { value: "quickAck", text: "Quick Ack" },
            { value: "registerStatus", text: "Register Status" },
            { value: "pushReject", text: "Push Reject" },
            { value: "quickAck2", text: "Quick Ack 2" },
            { value: "motionDetected", text: "Motion Detected" },
            { value: "motionStop", text: "Motion Stop" },
            { value: "peripheralFota", text: "Peripheral FOTA" },
            { value: "keepAlive", text: "Keep Alive" },
            { value: "gpsStatus", text: "GPS Status" },
            { value: "versionStatus", text: "Version Status" },
            { value: "cellStatus", text: "Cell Status" },
            { value: "powerStatus", text: "Power Status" },
            { value: "gpioStatus", text: "GPIO Status" },
            { value: "vehicleStatus", text: "Vehicle Status" },
            { value: "ipStatus", text: "IP Status" },
            { value: "registryStatus", text: "Registry Status" },
            { value: "counterStatus", text: "Counter Status" },
            { value: "accelerometerStatus", text: "Accelerometer Status" },
            { value: "sensorStatus", text: "Sensor Status" },
            { value: "commandResponse", text: "Command Response" },
            { value: "highFrequency", text: "High Frequency" }
        ],
        FordDevice: [
            { value: "IGNITION_ON", text: "Ignition On" },
            { value: "VEHICLE_MOVEMENT_STOPPED", text: "Vehicle Movement Stopped" },
            { value: "VEHICLE_MOVEMENT_STARTED", text: "Vehicle Movement Started" },
            { value: "SEAT_BELT_UNBUCKLED", text: "Seat Belt Unbuckled" },
            { value: "SEAT_BELT_BUCKLED", text: "Seat Belt Buckled" },
            { value: "EXCESSIVE_ACCELERATION", text: "Excessive Acceleration" },
            { value: "HARSH_ACCELERATION_EVENT", text: "Harsh Acceleration Event" },
            { value: "IGNITION_OFF", text: "Ignition Off" }
        ]
    };

    // Get event types based on selected device type for customTrip
    const getCustomTripEventTypes = () => {
        return deviceEventTypes[formData.deviceType] || [];
    };

    const getSortedCustomTripEventTypes = () => {
        return [...getCustomTripEventTypes()].sort((a, b) =>
            a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })
        );
    };

    // Device options configuration
    const deviceOptions = {
        trip: [
            { value: "XirgoHcfTrip", text: "XirgoHcf", tripName: "XirgoHcfTrips" },
            { value: "OnceEventTrip", text: "OnceEvent", tripName: "OnceEventTrips" },
            { value: "MontageIonTrip", text: "MontageIon", tripName: "MontageIonTrips" },
            { value: "QueclinkTrip", text: "Queclink", tripName: "QueclinkTrips" },
            { value: "MontageArsenalTrip", text: "MontageArsenal", tripName: "MontageArsenalTrips" },
            { value: "ApolloUdpTrip", text: "ApolloUdp", tripName: "ApolloUdpTrips" },
            // { value: "FordDeviceTrip", text: "FordDevice", tripName: "FordDeviceTrips" }
        ],
        customTrip: [
            { value: "XirgoHcf", text: "XirgoHcf" },
            { value: "OnceEvent", text: "OnceEvent" },
            { value: "MontageIon", text: "MontageIon" },
            { value: "Queclink", text: "Queclink" },
            { value: "MontageArsenal", text: "MontageArsenal" },
            { value: "ApolloUdp", text: "ApolloUdp" },
            { value: "FordDevice", text: "FordDevice" }
        ],
        event: [
            { value: "XirgoHcf", text: "XirgoHcf" },
            { value: "OnceEvent", text: "OnceEvent" },
            { value: "MontageIon", text: "MontageIon" },
            { value: "Queclink", text: "Queclink" },
            { value: "MontageArsenal", text: "MontageArsenal" },
            { value: "ApolloUdp", text: "ApolloUdp" },
            { value: "FordDevice", text: "FordDevice" }
        ],
        command: [
            { value: "XirgoHcf", text: "XirgoHcf" },
            { value: "MontageArsenal", text: "MontageArsenal" },
            { value: "FordDevice", text: "FordDevice" },
            { value: "MontageIon", text: "MontageIon" }
        ],
        commandResponse: [
            { value: "FordDevice", text: "FordDevice" },
            { value: "XirgoHcf", text: "XirgoHcf" },
            { value: "MontageArsenal", text: "MontageArsenal" },
            { value: "MontageIon", text: "MontageIon" }
        ]
    };

    const tripNames = {
        XirgoHcfTrip: ["speedTrip", "stopTrip", "IgnWithNoLatLngTrip", "TripWithNoLatLng", "DecelAccelTrip", "AllAlertTrip", "MeeraTrip", "seatBeltTrip", "seatBeltWithBelow5MphTrip", "seatBeltOffTrip", "defaultTrip"],
        OnceEventTrip: ["speedTrip", "defaultTrip"],
        MontageIonTrip: ["AllLoJackTrip", "LoJackSpeedTrip", "LoJackLowBatteryTrip", "LoJackTowTrip", "LoJackTamperTrip", "LoJackPowerTrip", "LoJackDefaultTrip", "speedTrip", "AllAlertTrip", "defaultTrip"],
        QueclinkTrip: ["HeartbeatTrip", "ExternalPowerOnTrip", "ResetTrip", "DefaultTrip"],
        MontageArsenalTrip: ["speedTrip", "AllLoJackTrip", "AllAlertTrip", "IgnWithNoLatLngTrip", "volumetricTrip", "TPMSTrip", "LowTirePressureTrip", "DoorEventTrip", "defaultTrip"],
        ApolloUdpTrip: ["AllLoJackTrip", "LoJackSpeedTrip", "LoJackLowBatteryTrip", "LoJackTowTrip", "LoJackTamperTrip", "LoJackPowerTrip", "LoJackDefaultTrip", "defaultTrip"],
        // FordDeviceTrip: ["defaultTrip"]
    };

    const deviceCommandTypes = {
        XirgoHcf: ["DriveReportCommand", "DeviceResetCommand", "LocateSmsCommand", "LocateCommand", "MaxSpeedSetCommand", "FirmwareQueryCommand", "PassThroughCommand", "StarterDisableCommand", "StarterEnableCommand", "DoorUnlockCommand", "ForceCheckInCommand", "RpmThresholdSetCommand", "DoorLockCommand"],
        MontageArsenal: ["NetworkQueryCommand", "ApnSetCommand", "AutoReportSetCommand", "ClearGeofenceHereCommand", "DisableAccelerometerStreamCommand", "DisableGpsStreamCommand", "DriveReportCommand", "EnableAccelerometerStreamCommand", "EnableGpsStreamCommand", "FirmwareQueryCommand"],
        FordDevice: ["remoteStart", "cancelRemoteStart", "unlock", "lock"],
        MontageIon: ["TrackModeSetCommand"]
    };

    const commandStatusTypes = {
        FordDevice: ["REQUESTED", "REQUEST_DELIVERY_QUEUED", "REQUEST_DELIVERY_IN_PROGRESS", "REQUEST_DELIVERY_TIMED_OUT", "REQUEST_DELIVERY_FAILURE", "EXPIRED", "SUCCESS", "FAILURE", "OEM_CLOUD_DELIVERY_IN_PROGRESS", "CANCELLATION_IN_PROGRESS", "CANCELLED"],
        XirgoHcf: ["XirgoHcf"],
        MontageArsenal: ["MontageArsenal"],
        MontageIon: ["TrackModeSetCommand"]
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            const file = files[0] || null;
            console.log('=== FILE INPUT CHANGED ===');
            console.log('Field name:', name);
            console.log('File selected:', file);
            if (file) {
                console.log('File name:', file.name);
                console.log('File size:', file.size);
                console.log('File type:', file.type);
            }
            setFormData(prev => {
                const newState = {
                    ...prev,
                    [name]: file
                };
                console.log('New formData.csvFileName:', newState.csvFileName);
                return newState;
            });
        } else {
            setFormData(prev => {
                const next = {
                    ...prev,
                    [name]: value
                };
                if (name === 'eventName' && value.trim().toLowerCase() !== 'dtc') {
                    next.dtcCode1 = '';
                    next.dtcCode2 = '';
                    next.dtcCode3 = '';
                }
                return next;
            });
        }

        // Clear error when user makes changes
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleIsTripChange = (valueOrEvent) => {
        const value = typeof valueOrEvent === 'string' ? valueOrEvent : valueOrEvent?.target?.value;
        setFormData(prev => ({
            ...prev,
            isTrip: value,
            deviceType: '',
            tripName: '',
            deviceCommandType: '',
            commandStatus: '',
            eventName: '',
            dtcCode1: '',
            dtcCode2: '',
            dtcCode3: ''
        }));
    };

    const handleDeviceTypeChange = (e) => {
        const newDeviceType = e.target.value;
        setFormData(prev => ({
            ...prev,
            deviceType: newDeviceType,
            tripName: '',
            deviceCommandType: '',
            commandStatus: '',
            eventName: '', // Clear eventName when device type changes for event option
            dtcCode1: '',
            dtcCode2: '',
            dtcCode3: ''
        }));
        // Clear trip events when device type changes for customTrip
        if (formData.isTrip === 'customTrip') {
            setTripEvents([]);
        }
    };

    // Modes that require a serial number + device type. For these we can
    // auto-detect the device type from the entered serial number.
    const isDeviceTypeMode = (mode) =>
        !!mode && mode !== 'findCommandStatus' && mode !== 'carrier';

    // Map a raw device type coming back from the backend to the option value
    // that is valid for the currently selected mode. The option `value` differs
    // per mode (e.g. trip mode uses "XirgoHcfTrip" while its label is "XirgoHcf"),
    // so we match against both the value and the display text.
    const resolveDeviceTypeOption = (rawType, mode) => {
        const options = deviceOptions[mode] || [];
        const normalized = String(rawType).trim().toLowerCase();
        return options.find(opt =>
            opt.value.toLowerCase() === normalized ||
            opt.text.toLowerCase() === normalized
        );
    };

    // Apply an auto-detected device type, reusing the same dependent-field reset
    // behaviour as a manual device type change.
    const applyDetectedDeviceType = (rawType, mode) => {
        const match = resolveDeviceTypeOption(rawType, mode);
        if (!match) {
            setDeviceTypeAutoDetected(false);
            setDeviceTypeLookup({
                loading: false,
                error: `Device type "${rawType}" is not available for the selected mode.`
            });
            return;
        }
        setFormData(prev => ({
            ...prev,
            deviceType: match.value,
            tripName: '',
            deviceCommandType: '',
            commandStatus: '',
            eventName: '',
            dtcCode1: '',
            dtcCode2: '',
            dtcCode3: ''
        }));
        if (mode === 'customTrip') {
            setTripEvents([]);
        }
        setErrors(prev => ({ ...prev, deviceType: '' }));
        setDeviceTypeAutoDetected(true);
        setDeviceTypeLookup({ loading: false, error: '' });
    };

    // Look up the device type for a serial number from the backend.
    const lookupDeviceType = async (serial, mode, environment) => {
        const requestId = ++deviceLookupSeqRef.current;
        setDeviceTypeLookup({ loading: true, error: '' });
        try {
            const params = new URLSearchParams({ serialNumber: serial });
            if (environment) {
                params.set('environment', environment);
            }
            const res = await apiFetch(
                `/api/device/type?${params.toString()}`,
                { headers: { Accept: 'application/json' } }
            );
            // Ignore responses for stale requests (serial changed meanwhile).
            if (requestId !== deviceLookupSeqRef.current) {
                return;
            }
            if (!res.ok) {
                // Try to surface the backend-provided error message (e.g. a 403
                // access-denied when the user isn't authorized for the selected
                // environment: { "error": "Access denied: ..." }).
                const errBody = await res.json().catch(() => null);
                if (requestId !== deviceLookupSeqRef.current) {
                    return;
                }
                const backendError = errBody && (errBody.error || errBody.message);
                let errorMessage;
                if (res.status === 403) {
                    errorMessage = backendError
                        || 'Access denied: you are not authorized for the selected environment.';
                } else {
                    errorMessage = backendError
                        || 'Could not determine device type for this serial number.';
                }
                setDeviceTypeAutoDetected(false);
                setDeviceTypeLookup({ loading: false, error: errorMessage });
                return;
            }
            const data = await res.json().catch(() => null);
            if (requestId !== deviceLookupSeqRef.current) {
                return;
            }
            const rawType = data && (data.deviceType || data.type || data.device);
            if (!rawType) {
                setDeviceTypeLookup({
                    loading: false,
                    error: 'No device type found for this serial number.'
                });
                return;
            }
            applyDetectedDeviceType(rawType, mode);
        } catch (e) {
            if (requestId !== deviceLookupSeqRef.current) {
                return;
            }
            console.warn('Device type lookup failed', e);
            setDeviceTypeLookup({ loading: false, error: 'Failed to look up device type.' });
        }
    };

    // Debounced auto-detection: whenever the serial number, mode or environment
    // changes and the current mode needs a device type, look it up from the backend.
    useEffect(() => {
        const serial = formData.serialNumber.trim();
        if (!serial || !isDeviceTypeMode(formData.isTrip) || !formData.environment) {
            // Nothing to look up (missing serial/environment or mode without a
            // device type) – clear any stale lookup feedback.
            deviceLookupSeqRef.current++;
            setDeviceTypeAutoDetected(false);
            setDeviceTypeLookup({ loading: false, error: '' });
            return;
        }
        const timer = setTimeout(() => {
            lookupDeviceType(serial, formData.isTrip, formData.environment);
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.serialNumber, formData.isTrip, formData.environment]);

    // Add new trip event for customTrip (max 30 events)
    const MAX_TRIP_EVENTS = 30;

    const addTripEvent = () => {
        if (tripEvents.length >= MAX_TRIP_EVENTS) {
            return;
        }
        const eventTypes = getCustomTripEventTypes();
        const defaultEventType = eventTypes.find(eventType => eventType.value === 'movement')?.value
            || eventTypes[0]?.value
            || '';
        const newEvent = {
            id: Date.now(),
            eventTypeId: defaultEventType,
            delay: hasCustomTripEventDelays ? DEFAULT_TRIP_EVENT_DELAY_SECONDS : '',
            latitude: '',
            longitude: '',
            altitude: TRIP_EVENT_FIELD_DEFAULTS.altitude,
            speed: TRIP_EVENT_FIELD_DEFAULTS.speed,
            heading: TRIP_EVENT_FIELD_DEFAULTS.heading,
            odometer: TRIP_EVENT_FIELD_DEFAULTS.odometer,
            satellites: TRIP_EVENT_FIELD_DEFAULTS.satellites,
            hdop: TRIP_EVENT_FIELD_DEFAULTS.hdop,
            rssi: TRIP_EVENT_FIELD_DEFAULTS.rssi,
            sequence: TRIP_EVENT_FIELD_DEFAULTS.sequence,
            distance: TRIP_EVENT_FIELD_DEFAULTS.distance,
            voltage: TRIP_EVENT_FIELD_DEFAULTS.voltage
        };
        setTripEvents(prev => [...prev, newEvent]);
    };

    // Remove trip event by id
    const removeTripEvent = (id) => {
        setTripEvents(prev => prev.filter(event => event.id !== id));
    };

    // Update trip event field
    const updateTripEvent = (id, field, value) => {
        setTripEvents(prev => {
            const updatedEvents = prev.map(event =>
                event.id === id ? { ...event, [field]: value } : event
            );

            if (field === 'delay' && value !== '') {
                return updatedEvents.map(event =>
                    event.delay === '' ? { ...event, delay: DEFAULT_TRIP_EVENT_DELAY_SECONDS } : event
                );
            }

            return updatedEvents;
        });
    };

    const toTripEventPayload = (event, index) => ({
        sequenceNumber: index + 1,
        eventName: event.eventTypeId,
        delay: event.delay !== '' && event.delay != null ? Number(event.delay) : undefined,
        latitude: event.latitude ? Number(event.latitude) : undefined,
        longitude: event.longitude ? Number(event.longitude) : undefined,
        altitude: event.altitude ? Number(event.altitude) : undefined,
        speed: event.speed ? Number(event.speed) : undefined,
        heading: event.heading ? Number(event.heading) : undefined,
        odometer: event.odometer ? Number(event.odometer) : undefined,
        satellites: event.satellites ? Number(event.satellites) : undefined,
        hdop: event.hdop ? Number(event.hdop) : undefined,
        rssi: event.rssi ? Number(event.rssi) : undefined,
        sequence: event.sequence ? Number(event.sequence) : undefined,
        distance: event.distance ? Number(event.distance) : undefined,
        voltage: event.voltage ? Number(event.voltage) : undefined
    });

    // Export trip events to CSV
    const exportTripsToCSV = () => {
        if (tripEvents.length === 0) {
            alert('No trip events to export. Please add events first.');
            return;
        }

        const csvContent = eventsToCSV(tripEvents);
        const timestamp = new Date().toISOString().split('T')[0];
        downloadCSV(csvContent, `trip_events_${formData.deviceType}_${timestamp}.csv`);
        setCsvImportSuccess('Trip events exported successfully!');
        setTimeout(() => setCsvImportSuccess(''), 3000);
    };

    // Import trip events from CSV
    const handleImportCSV = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const text = await file.text();
            const validEventTypes = getCustomTripEventTypes().map(t => t.value);
            const { events, errors } = parseTripsCSV(text, validEventTypes);

            if (errors.length > 0) {
                setCsvImportErrors(errors);
                setCsvImportSuccess('');
                return;
            }

            if (events.length === 0) {
                setCsvImportErrors(['No valid events found in CSV']);
                setCsvImportSuccess('');
                return;
            }

            // Check if we would exceed max events
            const totalEvents = tripEvents.length + events.length;
            if (totalEvents > MAX_TRIP_EVENTS) {
                setCsvImportErrors([`Cannot import ${events.length} events. Total would be ${totalEvents}, but maximum is ${MAX_TRIP_EVENTS}.`]);
                setCsvImportSuccess('');
                event.target.value = '';
                return;
            }

            // Per-event delays replace the shared wait time when any delay is supplied.
            const hasImportedDelays = events.some(importedEvent => importedEvent.delay !== '');
            const useEventDelays = hasImportedDelays || hasCustomTripEventDelays;

            // Add events to existing ones
            setTripEvents(prev => {
                const combinedEvents = [...prev, ...events];
                return useEventDelays
                    ? combinedEvents.map(tripEvent => ({
                        ...tripEvent,
                        delay: tripEvent.delay === ''
                            ? DEFAULT_TRIP_EVENT_DELAY_SECONDS
                            : tripEvent.delay
                    }))
                    : combinedEvents;
            });
            setCsvImportErrors([]);
            setCsvImportSuccess(`Successfully imported ${events.length} trip event(s)!`);

            // Clear the input
            event.target.value = '';

            // Clear success message after 5 seconds
            setTimeout(() => setCsvImportSuccess(''), 5000);
        } catch (err) {
            setCsvImportErrors([`Error reading file: ${err.message}`]);
            setCsvImportSuccess('');
            event.target.value = '';
        }
    };

    const sendTripEventsSequentially = async (events) => {
        releaseTripStopRef.current = false;
        setIsSubmitting(true);
        setSubmitMessage('');

        let completed = 0;
        const total = events.length;
        setReleaseTripRun({ status: 'running', completed, total });

        try {
            for (let index = 0; index < events.length; index++) {
                if (releaseTripStopRef.current) break;

                const eventPayload = toTripEventPayload(events[index], index);
                delete eventPayload.delay;
                const requestBody = new FormData();
                requestBody.append('environment', formData.environment);
                requestBody.append('serialNumber', formData.serialNumber.trim());
                requestBody.append('isTrip', 'customTrip');
                requestBody.append('deviceType', formData.deviceType || 'XirgoHcf');
                requestBody.append('tripEvents', JSON.stringify([eventPayload]));

                setReleaseTripRun({
                    status: 'running',
                    completed,
                    total,
                    current: index + 1,
                    eventPreview: events[index]
                });
                const response = await apiFetch('/api/eventgenerator', {
                    method: 'POST',
                    headers: { Accept: 'application/json' },
                    body: requestBody
                });

                let responseData = null;
                try {
                    responseData = await response.json();
                } catch (error) {
                    // Some successful responses do not include a JSON body.
                }
                if (!response.ok) {
                    throw new Error(responseData?.message || `Event ${index + 1} failed (HTTP ${response.status}).`);
                }

                completed = index + 1;
                setReleaseTripRun({ status: 'running', completed, total });

                const delaySeconds = Number(events[index].delay) || 0;
                if (index < events.length - 1 && delaySeconds > 0 && !releaseTripStopRef.current) {
                    const delayMs = delaySeconds * 1000;
                    const nextSendAt = Date.now() + delayMs;
                    setReleaseTripRun({
                        status: 'waiting',
                        completed,
                        total,
                        current: index + 2,
                        delayMs,
                        nextSendAt,
                        remainingMs: delayMs,
                        eventPreview: events[index + 1]
                    });
                    await new Promise(resolve => {
                        const timeoutId = setTimeout(() => {
                            releaseTripWaitRef.current = null;
                            resolve();
                        }, delayMs);
                        releaseTripWaitRef.current = () => {
                            clearTimeout(timeoutId);
                            releaseTripWaitRef.current = null;
                            resolve();
                        };
                    });
                }
            }

            if (releaseTripStopRef.current) {
                setReleaseTripRun({ status: 'stopped', completed, total });
            } else {
                setReleaseTripRun({ status: 'completed', completed, total });
            }
        } catch (error) {
            setReleaseTripRun({ status: 'failed', completed, total, message: error.message });
        } finally {
            releaseTripWaitRef.current = null;
            releaseTripStopRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleLoadReleaseTrip = async () => {
        if (['running', 'waiting'].includes(releaseTripRun?.status)) {
            releaseTripStopRef.current = true;
            setReleaseTripRun(prev => ({ ...prev, status: 'stopping' }));
            releaseTripWaitRef.current?.();
            return;
        }

        if (isReleaseTripActive) return;
        if (!formData.environment || !formData.serialNumber.trim()) {
            setReleaseTripRun({
                status: 'failed',
                completed: 0,
                total: 0,
                message: 'Select an environment and enter a serial number before loading the release trip.'
            });
            return;
        }

        if (!window.confirm(
            `Send the XirgoHcf release trip to serial ${formData.serialNumber} in ${formData.environment}? This replaces the current event list and submits events sequentially.`
        )) return;

        releaseTripStopRef.current = false;
        setReleaseTripRun({ status: 'loading', completed: 0, total: 0 });

        try {
            const csvResponse = await fetch(`${process.env.PUBLIC_URL || ''}/Xirgo_Trip_OnDemand.csv`);
            if (!csvResponse.ok) {
                throw new Error(`Could not load the release trip CSV (HTTP ${csvResponse.status}).`);
            }

            const csvText = await csvResponse.text();
            const validEventTypes = getCustomTripEventTypes().map(type => type.value);
            const { events, errors } = parseTripsCSV(csvText, validEventTypes);
            if (errors.length > 0 || events.length === 0) {
                throw new Error(errors[0] || 'The release trip CSV contains no events.');
            }

            if (events.length > MAX_TRIP_EVENTS) {
                throw new Error(`The release trip has ${events.length} events; the limit is ${MAX_TRIP_EVENTS}.`);
            }

            setTripEvents(events);
            setCsvImportErrors([]);
            setCsvImportSuccess('');
            await sendTripEventsSequentially(events);
        } catch (error) {
            setReleaseTripRun({ status: 'failed', completed: 0, total: 0, message: error.message });
        }
    };

    const downloadCsvTemplate = () => {
        const csvContent = "SequenceNumber,EventName,latitude,longitude,speed,odometer,mainMillivolts,battMillivolts,auxMillivolts,solarMillivolts,input1On,input1High,input2On,input2High,input3On,input3High,input4On,input4High,intBatteryPercentage\n1,ignitionOn,33.606198,-117.837003,50,500,12000,7500,12000,12000,true,false,true,false,false,false,false,false,56";

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'event_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const validateForm = () => {
        const newErrors = {};

        // Environment validation
        if (!formData.environment) {
            newErrors.environment = 'Please select an environment';
        } else if (
            allowedEnvironments.length > 0 &&
            !allowedEnvironments.includes(formData.environment)
        ) {
            newErrors.environment =
                `You are not authorized to send events to the ${formData.environment} environment.`;
        }

        // Serial number and device type validation (except for findCommandStatus)
        if (formData.isTrip !== 'findCommandStatus' && formData.isTrip !== 'carrier') {
            if (!formData.serialNumber.trim()) {
                newErrors.serialNumber = 'Serial Number is required';
            }
            if (!formData.deviceType) {
                newErrors.deviceType = 'Please select a device type';
            }
        }

        // Event-specific validation
        if (formData.isTrip === 'event' && !formData.eventName.trim()) {
            newErrors.eventName = 'Event Name is required for events';
        }

        // Trip-specific validation
        if (formData.isTrip === 'trip') {
            if (!formData.waitTime) {
                newErrors.waitTime = 'Please select at least one wait time';
            }
            if (!formData.tripName && !formData.csvFileName) {
                newErrors.tripName = 'Please provide either a Trip Name or CSV File';
            }
        }

        // Custom Trip validation - requires wait time
        if (formData.isTrip === 'customTrip') {
            if (!hasCustomTripEventDelays && !formData.waitTime) {
                newErrors.waitTime = 'Please select at least one wait time';
            }
        }

        // Command response and findCommandStatus validation
        if (formData.isTrip === 'commandResponse' || formData.isTrip === 'findCommandStatus') {
            if (!formData.commitId.trim()) {
                newErrors.commitId = 'Commit ID is required';
            }
        }

        // Previous date time validation
        if (formData.previousDateTime) {
            const selectedDateTime = new Date(formData.previousDateTime);
            const now = new Date();
            if (selectedDateTime > now) {
                newErrors.previousDateTime = 'Date and time cannot be in the future';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isReleaseTripActive) return;

        console.log('=== FORM SUBMIT ===');
        console.log('formData.csvFileName at submit:', formData.csvFileName);
        console.log('Full formData:', formData);

        if (!validateForm()) {
            return;
        }

        if (formData.isTrip === 'customTrip' && hasCustomTripEventDelays && tripEvents.length > 0) {
            await sendTripEventsSequentially(tripEvents);
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage('Sending Trip Data...');

        try {
            // Use FormData for all submissions
            const formDataToSend = new FormData();

            // Append all form fields
            formDataToSend.append('environment', formData.environment);
            formDataToSend.append('serialNumber', formData.serialNumber);
            formDataToSend.append('isTrip', formData.isTrip);
            formDataToSend.append('deviceType', formData.deviceType);

            if (formData.tripName) formDataToSend.append('tripName', formData.tripName);
            if (formData.deviceCommandType) formDataToSend.append('deviceCommandType', formData.deviceCommandType);
            if (formData.commandStatus) formDataToSend.append('commandStatus', formData.commandStatus);
            if (formData.eventName) formDataToSend.append('eventName', formData.eventName);
            if (formData.dtcCode1) formDataToSend.append('dtcCode1', formData.dtcCode1);
            if (formData.dtcCode2) formDataToSend.append('dtcCode2', formData.dtcCode2);
            if (formData.dtcCode3) formDataToSend.append('dtcCode3', formData.dtcCode3);
            if (formData.commitId) formDataToSend.append('commitId', formData.commitId);
            if (formData.seqNumber) formDataToSend.append('seqNumber', Number(formData.seqNumber));
            if (formData.tempCelsius) formDataToSend.append('tempCelsius', Number(formData.tempCelsius));
            if (formData.latitude && formData.isTrip !== 'customTrip') {
                formDataToSend.append('latitude', formData.latitude);
            }
            if (formData.longitude && formData.isTrip !== 'customTrip') {
                formDataToSend.append('longitude', formData.longitude);
            }
            if (formData.speed) formDataToSend.append('speed', formData.speed);
            if (formData.odometer) formDataToSend.append('odometer', formData.odometer);
            if (formData.mainMillivolts) formDataToSend.append('mainMillivolts', formData.mainMillivolts);
            if (formData.battMillivolts) formDataToSend.append('battMillivolts', formData.battMillivolts);
            if (formData.auxMillivolts) formDataToSend.append('auxMillivolts', formData.auxMillivolts);
            if (formData.solarMillivolts) formDataToSend.append('solarMillivolts', formData.solarMillivolts);
            formDataToSend.append('cargoLoaded', formData.cargoLoaded === 'true');
            if (formData.previousDateTime) formDataToSend.append('previousDateTime', formData.previousDateTime);
            if (formData.waitTime && !(formData.isTrip === 'customTrip' && hasCustomTripEventDelays)) {
                formDataToSend.append('waitTime', Number(formData.waitTime));
            }
            if (formData.deviceId) formDataToSend.append('deviceId', formData.deviceId);
            if (formData.deviceCommandValue) formDataToSend.append('deviceCommandValue', formData.deviceCommandValue);
            if (formData.commandMode) formDataToSend.append('commandMode', formData.commandMode);
            if (formData.commandDuration) formDataToSend.append('commandDuration', formData.commandDuration);
            if (formData.networkProvider) formDataToSend.append('networkProvider', formData.networkProvider);
            if (formData.carrierStatus) formDataToSend.append('carrierStatus', formData.carrierStatus);

            // Append the CSV file if present
            if (formData.csvFileName) {
                console.log('CSV File being sent:', formData.csvFileName);
                console.log('CSV File name:', formData.csvFileName.name);
                console.log('CSV File size:', formData.csvFileName.size);
                formDataToSend.append('csvFileName', formData.csvFileName, formData.csvFileName.name);
            } else {
                console.log('No CSV file selected, formData.csvFileName is:', formData.csvFileName);
            }

            // Debug: Log all FormData entries
            console.log('FormData entries:');
            for (let [key, value] of formDataToSend.entries()) {
                console.log(`  ${key}:`, value);
            }

            // Add tripEvents for customTrip
            if (formData.isTrip === 'customTrip' && tripEvents.length > 0) {
                const tripEventsData = tripEvents.map(toTripEventPayload);
                formDataToSend.append('tripEvents', JSON.stringify(tripEventsData));
            }

            const res = await apiFetch('/api/eventgenerator', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                },
                body: formDataToSend,
            });

            // Some error responses may not be JSON; guard against parse failures.
            let data = null;
            try {
                data = await res.json();
            } catch (parseErr) {
                console.warn('Failed to parse response JSON', parseErr);
            }

            if (!res.ok) {
                // Backend returns failures as { jobId, message, status } (and sometimes { error }).
                // Surface the most specific message available instead of a generic fallback.
                const errMsg =
                    (data && (data.message || data.error)) ||
                    `Failed to submit event (HTTP ${res.status})`;
                setSubmitMessage('');
                setIsSubmitting(false);

                // If the backend gave us a jobId, show the failure on the Results page
                // (consistent with successful submissions) instead of a popup alert.
                if (data && data.jobId) {
                    const failRequestInfo = {
                        environment: formData.environment,
                        serialNumber: formData.serialNumber,
                        isTrip: formData.isTrip,
                        deviceType: formData.deviceType,
                        tripName: formData.tripName || undefined,
                        waitTime: formData.waitTime || undefined,
                        csvFileName: formData.csvFileName ? formData.csvFileName.name : undefined
                    };
                    const failRequestParam = encodeURIComponent(JSON.stringify(failRequestInfo));
                    const failStatus = encodeURIComponent(data.status || 'failed');
                    const failMessageParam = encodeURIComponent(errMsg);
                    const failPayloadParam = data.payload
                        ? encodeURIComponent(JSON.stringify(data.payload))
                        : failRequestParam;
                    navigate(`/api/eventgenerator/results?jobId=${encodeURIComponent(data.jobId)}&status=${failStatus}&message=${failMessageParam}&request=${failPayloadParam}`);
                    return;
                }

                // No jobId to display – fall back to an alert.
                alert(errMsg);
                return;
            }

            setIsSubmitting(false);
            setSubmitMessage('');

            if (!data) {
                alert('Received an empty or invalid response from the server.');
                return;
            }

            // Build request info for navigation
            const requestInfo = {
                environment: formData.environment,
                serialNumber: formData.serialNumber,
                isTrip: formData.isTrip,
                deviceType: formData.deviceType,
                tripName: formData.tripName || undefined,
                waitTime: formData.waitTime || undefined,
                csvFileName: formData.csvFileName ? formData.csvFileName.name : undefined
            };
            const requestParam = encodeURIComponent(JSON.stringify(requestInfo));
            if (data.jobId) {
                const jobStatus = data.status === 'completed' ? 'success' : (data.status ?? 'processing');
                const messageParam = data.message ? encodeURIComponent(data.message) : '';
                const payloadParam = data.payload ? encodeURIComponent(JSON.stringify(data.payload)) : requestParam;
                navigate(`/api/eventgenerator/results?jobId=${encodeURIComponent(data.jobId)}&status=${encodeURIComponent(jobStatus)}&message=${messageParam}&request=${payloadParam}`);
            } else if (data.type === 'json' && data.data) {
                navigate(`/api/eventgenerator/results?json=${encodeURIComponent(data.data)}&request=${requestParam}`);
            } else if (data.type === 'commitId' && data.commitId) {
                navigate(`/api/eventgenerator/results?commitId=${encodeURIComponent(data.commitId)}&request=${requestParam}`);
            } else {
                alert('Unknown response received from server');
            }
        } catch (error) {
            console.error('Submission error:', error);
            setIsSubmitting(false);
            setSubmitMessage('');
            alert('Unexpected error occurred while submitting the event.');
        }
    };

    const handleLogout = async () => {
        try {
            await apiFetch('/api/auth/logout', { method: 'POST' });
        } catch (e) {
            console.warn('Logout request failed', e);
        } finally {
            window.location.assign('/login?logout');
        }
    };

    const handleCancel = async () => {
        try {
            if (String(formData.isTrip || '').trim().toLowerCase() === 'trip' && formData.serialNumber) {
                await apiFetch('/api/trip/cancel', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ serialNumber: formData.serialNumber }),
                });
            }
        } catch (e) {
            // best-effort cancel; ignore errors
            console.warn('Trip cancel request failed', e);
        }
        // Reset form data to initial state
        setFormData({
            environment: '',
            serialNumber: '',
            isTrip: '',
            deviceType: '',
            tripName: '',
            deviceCommandType: '',
            commandStatus: '',
            csvFileName: null,
            eventName: '',
            dtcCode1: '',
            dtcCode2: '',
            dtcCode3: '',
            commitId: '',
            seqNumber: EVENT_FIELD_DEFAULTS.seqNumber,
            tempCelsius: EVENT_FIELD_DEFAULTS.tempCelsius,
            latitude: EVENT_FIELD_DEFAULTS.latitude,
            longitude: EVENT_FIELD_DEFAULTS.longitude,
            speed: EVENT_FIELD_DEFAULTS.speed,
            odometer: EVENT_FIELD_DEFAULTS.odometer,
            mainMillivolts: EVENT_FIELD_DEFAULTS.mainMillivolts,
            battMillivolts: EVENT_FIELD_DEFAULTS.battMillivolts,
            auxMillivolts: EVENT_FIELD_DEFAULTS.auxMillivolts,
            solarMillivolts: EVENT_FIELD_DEFAULTS.solarMillivolts,
            cargoLoaded: 'true',
            previousDateTime: '',
            waitTime: '',
            deviceId: '',
            deviceCommandValue: '',
            commandMode: '',
            commandDuration: '',
            carrierStatus: 'Enable',
            networkProvider: ''
        });
        setErrors({});
        setSubmitMessage('');
        setDeviceTypeAutoDetected(false);
        setDeviceTypeLookup({ loading: false, error: '' });
    };

    // Get available device types based on selected trip mode
    const getDeviceTypes = () => {
        return deviceOptions[formData.isTrip] || [];
    };

    // Get available trip names based on selected device
    const getTripNames = () => {
        return tripNames[formData.deviceType] || [];
    };

    // Get available command types based on selected device
    const getDeviceCommandTypes = () => {
        return deviceCommandTypes[formData.deviceType] || [];
    };

    // Get available command status types based on selected device
    const getCommandStatusTypes = () => {
        return commandStatusTypes[formData.deviceType] || [];
    };

    return (
        <div className="event-generator-container">
            <button type="button" className="logout-button" onClick={handleLogout}>
                Logout
            </button>

            <div className="content-wrapper">
                {/* Left Section - Form (75%) */}
                <div className="form-section">
                    <div className="event-form">
                        <h2>Event Generator Form</h2>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="environment">Environment:<span className="required-asterisk">*</span></label>
                                <select
                                    id="environment"
                                    name="environment"
                                    value={formData.environment}
                                    onChange={handleChange}
                                >
                                    <option value="">Select environment...</option>
                                    {environmentOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.text}</option>
                                    ))}
                                </select>
                                {errors.environment && <span className="error">{errors.environment}</span>}
                            </div>


                            <div className="trip-checkboxes">
                                <legend>Event Types</legend>
                                <div className="trip-checkboxes-options">
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="trip"
                                            checked={formData.isTrip === 'trip'}
                                            onChange={() => handleIsTripChange('trip')}
                                        />
                                        Trip
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="customTrip"
                                            checked={formData.isTrip === 'customTrip'}
                                            onChange={() => handleIsTripChange('customTrip')}
                                        />
                                        Create Own Trip
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="event"
                                            checked={formData.isTrip === 'event'}
                                            onChange={() => handleIsTripChange('event')}
                                        />
                                        Event
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="command"
                                            checked={formData.isTrip === 'command'}
                                            onChange={() => handleIsTripChange('command')}
                                        />
                                        Command
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="commandResponse"
                                            checked={formData.isTrip === 'commandResponse'}
                                            onChange={() => handleIsTripChange('commandResponse')}
                                        />
                                        CommandResponse
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="findCommandStatus"
                                            checked={formData.isTrip === 'findCommandStatus'}
                                            onChange={() => handleIsTripChange('findCommandStatus')}
                                        />
                                        CommandStatus
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="isTrip"
                                            value="carrier"
                                            checked={formData.isTrip === 'carrier'}
                                            onChange={() => handleIsTripChange('carrier')}
                                        />
                                        Carrier
                                    </label>
                                </div>
                            </div>
                            {formData.isTrip !== 'findCommandStatus' && formData.isTrip !== 'carrier' && (
                                <div className="form-group">
                                    <label htmlFor="serialNumber">Serial Number:<span className="required-asterisk">*</span></label>
                                    <input
                                        type="text"
                                        id="serialNumber"
                                        name="serialNumber"
                                        value={formData.serialNumber}
                                        onChange={handleChange}
                                    />
                                    {errors.serialNumber && <span className="error">{errors.serialNumber}</span>}
                                </div>
                            )}

                            {formData.isTrip && formData.isTrip !== 'findCommandStatus' && formData.isTrip !== 'carrier' && (
                                <div className="form-group">
                                    <label htmlFor="deviceType">Device Type:<span className="required-asterisk">*</span></label>
                                    <select
                                        id="deviceType"
                                        name="deviceType"
                                        value={formData.deviceType}
                                        onChange={handleDeviceTypeChange}
                                        disabled={deviceTypeAutoDetected}
                                        className={deviceTypeAutoDetected ? 'auto-populated' : undefined}
                                        title={deviceTypeAutoDetected ? 'Auto-populated from the serial number' : undefined}
                                    >
                                        <option value="">Select device type...</option>
                                        {getDeviceTypes().map(option => (
                                            <option key={option.value} value={option.value}>{option.text}</option>
                                        ))}
                                    </select>
                                    {deviceTypeLookup.loading && (
                                        <span className="hint">Detecting device type…</span>
                                    )}
                                    {!deviceTypeLookup.loading && deviceTypeAutoDetected && (
                                        <span className="hint">Auto-populated from serial number (read-only)</span>
                                    )}
                                    {!deviceTypeLookup.loading && deviceTypeLookup.error && (
                                        <span className="error">{deviceTypeLookup.error}</span>
                                    )}
                                    {errors.deviceType && <span className="error">{errors.deviceType}</span>}
                                </div>
                            )}

                            {/* Custom Trip Event Container */}
                            {formData.isTrip === 'customTrip' && formData.deviceType && (
                                <div className="trip-event-container">
                                    <div className="trip-event-header">
                                        <legend>Trip Events ({tripEvents.length}/{MAX_TRIP_EVENTS})</legend>
                                        <div className="trip-event-buttons">
                                            <button
                                                type="button"
                                                className="add-event-btn"
                                                onClick={addTripEvent}
                                                disabled={tripEvents.length >= MAX_TRIP_EVENTS}
                                            >
                                                + Add Event
                                            </button>
                                            <button
                                                type="button"
                                                className="csv-btn export-btn"
                                                onClick={exportTripsToCSV}
                                                disabled={tripEvents.length === 0}
                                                title="Export current trip events to CSV file"
                                            >
                                                📥 Export CSV
                                            </button>
                                            <label className="csv-btn import-btn" title="Import trip events from CSV file">
                                                📤 Import CSV
                                                <input
                                                    type="file"
                                                    accept=".csv"
                                                    onChange={handleImportCSV}
                                                    style={{ display: 'none' }}
                                                />
                                            </label>
                                            {formData.deviceType === 'XirgoHcf' && (
                                                <button
                                                    type="button"
                                                    className="csv-btn release-trip-btn"
                                                    onClick={handleLoadReleaseTrip}
                                                    disabled={['loading', 'stopping'].includes(releaseTripRun?.status)}
                                                >
                                                    {['running', 'waiting'].includes(releaseTripRun?.status)
                                                        ? 'Stop Release Trip'
                                                        : releaseTripRun?.status === 'loading'
                                                            ? 'Loading Release Trip...'
                                                            : releaseTripRun?.status === 'stopping'
                                                                ? 'Stopping...'
                                                                : 'Load Release Trip'}
                                                </button>
                                            )}
                                        </div>
                                        {releaseTripRun && (
                                            <div className={`release-trip-status ${releaseTripRun.status}`} role="status" aria-live="polite">
                                                {releaseTripRun.status === 'loading' && 'Loading release trip...'}
                                                {releaseTripRun.status === 'running' && `Sending event ${releaseTripRun.current || releaseTripRun.completed + 1} of ${releaseTripRun.total} (${releaseTripRun.completed} accepted)`}
                                                {releaseTripRun.status === 'waiting' && `Waiting to send event ${releaseTripRun.current} of ${releaseTripRun.total} (${releaseTripRun.completed} accepted)`}
                                                {releaseTripRun.status === 'stopping' && `Stopping after ${releaseTripRun.completed} of ${releaseTripRun.total} accepted events...`}
                                                {releaseTripRun.status === 'stopped' && `Stopped after ${releaseTripRun.completed} of ${releaseTripRun.total} accepted events.`}
                                                {releaseTripRun.status === 'completed' && `Trip complete: ${releaseTripRun.completed} events accepted.`}
                                                {releaseTripRun.status === 'failed' && releaseTripRun.message}
                                            </div>
                                        )}
                                        {releaseTripRun?.status === 'waiting' && (
                                            <div className="release-trip-live-row">
                                                <div className="release-trip-countdown">
                                                    <span>{`Next event in ${(releaseTripRun.remainingMs / 1000).toFixed(1)} seconds`}</span>
                                                    <progress
                                                        aria-label="Time until next event"
                                                        max={releaseTripRun.delayMs}
                                                        value={releaseTripRun.delayMs - releaseTripRun.remainingMs}
                                                    />
                                                </div>
                                                {releaseTripRun.eventPreview && (
                                                    <div className="release-trip-event-preview">
                                                        <strong>Next event</strong>
                                                        <span>Type: {releaseTripRun.eventPreview.eventTypeId || '—'}</span>
                                                        <span>Lat: {releaseTripRun.eventPreview.latitude !== '' && releaseTripRun.eventPreview.latitude != null ? releaseTripRun.eventPreview.latitude : '—'}</span>
                                                        <span>Lng: {releaseTripRun.eventPreview.longitude !== '' && releaseTripRun.eventPreview.longitude != null ? releaseTripRun.eventPreview.longitude : '—'}</span>
                                                        <span>Speed: {releaseTripRun.eventPreview.speed !== '' && releaseTripRun.eventPreview.speed != null ? releaseTripRun.eventPreview.speed : '—'}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {releaseTripRun?.status === 'running' && releaseTripRun.eventPreview && (
                                            <div className="release-trip-event-preview sending">
                                                <strong>Sending event</strong>
                                                <span>Type: {releaseTripRun.eventPreview.eventTypeId || '—'}</span>
                                                <span>Lat: {releaseTripRun.eventPreview.latitude !== '' && releaseTripRun.eventPreview.latitude != null ? releaseTripRun.eventPreview.latitude : '—'}</span>
                                                <span>Lng: {releaseTripRun.eventPreview.longitude !== '' && releaseTripRun.eventPreview.longitude != null ? releaseTripRun.eventPreview.longitude : '—'}</span>
                                                <span>Speed: {releaseTripRun.eventPreview.speed !== '' && releaseTripRun.eventPreview.speed != null ? releaseTripRun.eventPreview.speed : '—'}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* CSV Import/Export Status Messages */}
                                    {csvImportSuccess && (
                                        <div className="csv-success-message">
                                            ✓ {csvImportSuccess}
                                        </div>
                                    )}
                                    {csvImportErrors.length > 0 && (
                                        <div className="csv-error-messages">
                                            <strong>Import Errors:</strong>
                                            <ul>
                                                {csvImportErrors.map((err, idx) => (
                                                    <li key={idx}>{err}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {tripEvents.map((event, index) => (
                                        <div key={event.id} className="trip-event">
                                            <div className="trip-event-row">
                                                <span className="event-number">Event {index + 1}</span>
                                                <button
                                                    type="button"
                                                    className="remove-event-btn"
                                                    onClick={() => removeTripEvent(event.id)}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                            <div className="trip-event-fields">
                                                <div className="field-group">
                                                        <label>Event Type<span className="required-asterisk">*</span></label>
                                                        <select
                                                            value={event.eventTypeId}
                                                            onChange={(e) => updateTripEvent(event.id, 'eventTypeId', e.target.value)}
                                                        >
                                                            {getSortedCustomTripEventTypes().map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.text}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                <div className="field-group">
                                                        <label htmlFor={`trip-event-delay-${event.id}`}>Delay (seconds)</label>
                                                    <input
                                                        type="number"
                                                            id={`trip-event-delay-${event.id}`}
                                                        min="0"
                                                        step="any"
                                                        value={event.delay}
                                                        onChange={(e) => updateTripEvent(event.id, 'delay', e.target.value)}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Latitude</label>
                                                    <input
                                                        type="text"
                                                        value={event.latitude}
                                                        onChange={(e) => updateTripEvent(event.id, 'latitude', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.latitude}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Longitude</label>
                                                    <input
                                                        type="text"
                                                        value={event.longitude}
                                                        onChange={(e) => updateTripEvent(event.id, 'longitude', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.longitude}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Altitude</label>
                                                    <input
                                                        type="text"
                                                        value={event.altitude}
                                                        onChange={(e) => updateTripEvent(event.id, 'altitude', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.altitude}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Speed</label>
                                                    <input
                                                        type="text"
                                                        value={event.speed}
                                                        onChange={(e) => updateTripEvent(event.id, 'speed', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.speed}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Heading</label>
                                                    <input
                                                        type="text"
                                                        value={event.heading}
                                                        onChange={(e) => updateTripEvent(event.id, 'heading', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.heading}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Odometer</label>
                                                    <input
                                                        type="text"
                                                        value={event.odometer}
                                                        onChange={(e) => updateTripEvent(event.id, 'odometer', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.odometer}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Satellites</label>
                                                    <input
                                                        type="text"
                                                        value={event.satellites}
                                                        onChange={(e) => updateTripEvent(event.id, 'satellites', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.satellites}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>HDOP</label>
                                                    <input
                                                        type="text"
                                                        value={event.hdop}
                                                        onChange={(e) => updateTripEvent(event.id, 'hdop', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.hdop}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>RSSI</label>
                                                    <input
                                                        type="text"
                                                        value={event.rssi}
                                                        onChange={(e) => updateTripEvent(event.id, 'rssi', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.rssi}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Sequence</label>
                                                    <input
                                                        type="text"
                                                        value={event.sequence}
                                                        onChange={(e) => updateTripEvent(event.id, 'sequence', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.sequence}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Distance</label>
                                                    <input
                                                        type="text"
                                                        value={event.distance}
                                                        onChange={(e) => updateTripEvent(event.id, 'distance', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.distance}`}
                                                    />
                                                </div>
                                                <div className="field-group">
                                                    <label>Voltage</label>
                                                    <input
                                                        type="text"
                                                        value={event.voltage}
                                                        onChange={(e) => updateTripEvent(event.id, 'voltage', e.target.value)}
                                                        placeholder={`${tripEventFieldDefaults.voltage}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {tripEvents.length === 0 && (
                                        <p className="no-events-message">No events added. Click "Add Event" to create trip events.</p>
                                    )}
                                </div>
                            )}

                            {formData.isTrip === 'trip' && formData.deviceType && getTripNames().length > 0 && (
                                <div className="form-group">
                                    <label htmlFor="tripName">Select TripName or CSV File:<span className="required-asterisk">*</span></label>
                                    <select
                                        id="tripName"
                                        name="tripName"
                                        value={formData.tripName}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Trip Name...</option>
                                        {getTripNames().map(name => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                    {errors.tripName && <span className="error">{errors.tripName}</span>}
                                </div>
                            )}

                            {formData.isTrip === 'trip' && (
                                <div className="form-group csv-group">
                                    <button type="button" onClick={downloadCsvTemplate} className="download-csv-btn">
                                        Download CSV Template
                                    </button>
                                    <label htmlFor="csvFileName">CSV File:</label>
                                    <input
                                        type="file"
                                        id="csvFileName"
                                        name="csvFileName"
                                        accept=".csv"
                                        onChange={handleChange}
                                    />
                                </div>
                            )}

                            {formData.isTrip === 'command' && formData.deviceType && getDeviceCommandTypes().length > 0 && (
                                <div className="form-group">
                                    <label htmlFor="deviceCommandType">Device Command Type:</label>
                                    <select
                                        id="deviceCommandType"
                                        name="deviceCommandType"
                                        value={formData.deviceCommandType}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Command Types...</option>
                                        {getDeviceCommandTypes().map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                    {errors.deviceCommandType && <span className="error">{errors.deviceCommandType}</span>}
                                </div>
                            )}

                            {formData.isTrip === 'commandResponse' && formData.deviceType && getCommandStatusTypes().length > 0 && (
                                <div className="form-group">
                                    <label htmlFor="commandStatus">Command Status Type:</label>
                                    <select
                                        id="commandStatus"
                                        name="commandStatus"
                                        value={formData.commandStatus}
                                        onChange={handleChange}
                                    >
                                        <option value="">Command Status Type...</option>
                                        {getCommandStatusTypes().map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                    {errors.commandStatus && <span className="error">{errors.commandStatus}</span>}
                                </div>
                            )}

                            {formData.isTrip === 'event' && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="eventName">Event Name:<span className="required-asterisk">*</span></label>
                                        <input
                                            type="text"
                                            id="eventName"
                                            name="eventName"
                                            list="eventNameOptions"
                                            value={formData.eventName}
                                            onChange={handleChange}
                                            placeholder="Select or enter event name..."
                                        />
                                        <datalist id="eventNameOptions">
                                            {getSortedCustomTripEventTypes().map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.text}</option>
                                            ))}
                                        </datalist>
                                        {errors.eventName && <span className="error">{errors.eventName}</span>}
                                    </div>

                                    {String(formData.deviceType || '').toLowerCase() === 'xirgohcf' &&
                                        String(formData.eventName || '').trim().toLowerCase() === 'dtc' && (
                                            <>
                                                <div className="form-group">
                                                    <label htmlFor="dtcCode1">DTC Code 1:</label>
                                                    <select
                                                        id="dtcCode1"
                                                        name="dtcCode1"
                                                        value={formData.dtcCode1}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select DTC code...</option>
                                                        {DTC_EVENT_CODES.map(({ code, description }) => (
                                                            <option key={code} value={code}>{`${code}: ${description}`}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="form-group">
                                                    <label htmlFor="dtcCode2">DTC Code 2:</label>
                                                    <select
                                                        id="dtcCode2"
                                                        name="dtcCode2"
                                                        value={formData.dtcCode2}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select DTC code...</option>
                                                        {DTC_EVENT_CODES.map(({ code, description }) => (
                                                            <option key={code} value={code}>{`${code}: ${description}`}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="form-group">
                                                    <label htmlFor="dtcCode3">DTC Code 3:</label>
                                                    <select
                                                        id="dtcCode3"
                                                        name="dtcCode3"
                                                        value={formData.dtcCode3}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select DTC code...</option>
                                                        {DTC_EVENT_CODES.map(({ code, description }) => (
                                                            <option key={code} value={code}>{`${code}: ${description}`}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                    <div className="form-group">
                                        <label htmlFor="seqNumber">Sequence Number:</label>
                                        <input
                                            type="text"
                                            id="seqNumber"
                                            name="seqNumber"
                                            value={formData.seqNumber}
                                            onChange={handleChange}
                                            placeholder={`${eventFieldDefaults.seqNumber}`}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="tempCelsius">Temperature in Celsius:</label>
                                        <input
                                            type="text"
                                            id="tempCelsius"
                                            name="tempCelsius"
                                            value={formData.tempCelsius}
                                            onChange={handleChange}
                                            placeholder={`${eventFieldDefaults.tempCelsius}`}
                                        />
                                    </div>

                                    <div className="coordinate-row">
                                        <div className="coord-input">
                                            <label htmlFor="latitude">Lat:</label>
                                            <input
                                                type="text"
                                                id="latitude"
                                                name="latitude"
                                                value={formData.latitude}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.latitude}`}
                                            />
                                        </div>
                                        <div className="coord-input">
                                            <label htmlFor="longitude">Lng:</label>
                                            <input
                                                type="text"
                                                id="longitude"
                                                name="longitude"
                                                value={formData.longitude}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.longitude}`}
                                            />
                                        </div>
                                        <div className="coord-input">
                                            <label htmlFor="speed">Speed:</label>
                                            <input
                                                type="text"
                                                id="speed"
                                                name="speed"
                                                value={formData.speed}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.speed}`}
                                            />
                                        </div>
                                        <div className="coord-input">
                                            <label htmlFor="odometer">Odo:</label>
                                            <input
                                                type="text"
                                                id="odometer"
                                                name="odometer"
                                                value={formData.odometer}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.odometer}`}
                                            />
                                        </div>
                                    </div>

                                    <div className="battery-row">
                                        <div className="battery-input">
                                            <label htmlFor="mainMillivolts">mainMillivolt:</label>
                                            <input
                                                type="text"
                                                id="mainMillivolts"
                                                name="mainMillivolts"
                                                value={formData.mainMillivolts}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.mainMillivolts}`}
                                            />
                                        </div>
                                        <div className="battery-input">
                                            <label htmlFor="battMillivolts">battMillivolt:</label>
                                            <input
                                                type="text"
                                                id="battMillivolts"
                                                name="battMillivolts"
                                                value={formData.battMillivolts}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.battMillivolts}`}
                                            />
                                        </div>
                                        <div className="battery-input">
                                            <label htmlFor="auxMillivolts">auxMillivolt:</label>
                                            <input
                                                type="text"
                                                id="auxMillivolts"
                                                name="auxMillivolts"
                                                value={formData.auxMillivolts}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.auxMillivolts}`}
                                            />
                                        </div>
                                        <div className="battery-input">
                                            <label htmlFor="solarMillivolts">solarMillivolt:</label>
                                            <input
                                                type="text"
                                                id="solarMillivolts"
                                                name="solarMillivolts"
                                                value={formData.solarMillivolts}
                                                onChange={handleChange}
                                                placeholder={`${eventFieldDefaults.solarMillivolts}`}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="cargoLoaded">CargoLoaded & SeatBelt:</label>
                                        <select
                                            id="cargoLoaded"
                                            name="cargoLoaded"
                                            value={formData.cargoLoaded}
                                            onChange={handleChange}
                                        >
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="previousDateTime">Previous Date & Time (MM/DD/YYYY, HH:MM):</label>
                                        <input
                                            type="datetime-local"
                                            id="previousDateTime"
                                            name="previousDateTime"
                                            value={formData.previousDateTime}
                                            onChange={handleChange}
                                        />
                                        {errors.previousDateTime && <span className="error">{errors.previousDateTime}</span>}
                                    </div>
                                </>
                            )}

                            {(formData.isTrip === 'trip' || (formData.isTrip === 'customTrip' && !hasCustomTripEventDelays)) && (
                                <div className="wait-time-container">
                                    <legend>Wait Time Between Events (Minutes):<span className="required-asterisk">*</span></legend>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value=".1"
                                            checked={formData.waitTime === '.1'}
                                            onChange={handleChange}
                                        />
                                        5 Sec
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value=".2"
                                            checked={formData.waitTime === '.2'}
                                            onChange={handleChange}
                                        />
                                        10 Sec
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value=".5"
                                            checked={formData.waitTime === '.5'}
                                            onChange={handleChange}
                                        />
                                        30 Sec
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value="1"
                                            checked={formData.waitTime === '1'}
                                            onChange={handleChange}
                                        />
                                        1 Min
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value="2"
                                            checked={formData.waitTime === '2'}
                                            onChange={handleChange}
                                        />
                                        2 Min
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value="3"
                                            checked={formData.waitTime === '3'}
                                            onChange={handleChange}
                                        />
                                        3 Min
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value="5"
                                            checked={formData.waitTime === '5'}
                                            onChange={handleChange}
                                        />
                                        5 Min
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="waitTime"
                                            value="10"
                                            checked={formData.waitTime === '10'}
                                            onChange={handleChange}
                                        />
                                        10 Min
                                    </label>
                                    {errors.waitTime && <span className="error">{errors.waitTime}</span>}
                                </div>
                            )}

                            {(formData.isTrip === 'commandResponse' || formData.isTrip === 'findCommandStatus') && (
                                <div className="form-group">
                                    <label htmlFor="commitId">Commit ID:<span className="required-asterisk">*</span></label>
                                    <input
                                        type="text"
                                        id="commitId"
                                        name="commitId"
                                        value={formData.commitId}
                                        onChange={handleChange}
                                    />
                                    {errors.commitId && <span className="error">{errors.commitId}</span>}
                                </div>
                            )}

                            {formData.isTrip === 'command' && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="deviceId">Device Id:</label>
                                        <input
                                            type="text"
                                            id="deviceId"
                                            name="deviceId"
                                            value={formData.deviceId}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="deviceCommandValue">DeviceCommand Value:</label>
                                        <input
                                            type="text"
                                            id="deviceCommandValue"
                                            name="deviceCommandValue"
                                            value={formData.deviceCommandValue}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {formData.deviceType === 'MontageIon' && (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="commandMode">Command Mode for MontageIon:</label>
                                                <select
                                                    id="commandMode"
                                                    name="commandMode"
                                                    value={formData.commandMode}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select Command Mode...</option>
                                                    <option value="QUICK">QUICK</option>
                                                    <option value="SENSOR">SENSOR</option>
                                                    <option value="DAILY">DAILY</option>
                                                    <option value="RECOVERY">RECOVERY</option>
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="commandDuration">Command Duration for MontageIon:</label>
                                                <select
                                                    id="commandDuration"
                                                    name="commandDuration"
                                                    value={formData.commandDuration}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select Duration...</option>
                                                    <option value="0">0</option>
                                                    <option value="86400">86400</option>
                                                    <option value="86401">86401</option>
                                                    <option value="172800">172800</option>
                                                    <option value="172801">172801</option>
                                                    <option value="259200">259200</option>
                                                    <option value="259201">259201</option>
                                                    <option value="2147483647">2147483647 (Integer.MAX)</option>
                                                    <option value="-2147483648">-2147483648 (Integer.MIN)</option>
                                                    <option value="-1">-1</option>
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {formData.isTrip === 'carrier' && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="deviceId">Device Id:</label>
                                        <input
                                            type="text"
                                            id="deviceId"
                                            name="deviceId"
                                            value={formData.deviceId}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="carrierStatus">CarrierStatus:</label>
                                        <select
                                            id="carrierStatus"
                                            name="carrierStatus"
                                            value={formData.carrierStatus}
                                            onChange={handleChange}
                                        >
                                            <option value="Enable">Enable</option>
                                            <option value="Disable">Disable</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="networkProvider">Network Provider:</label>
                                        <select
                                            id="networkProvider"
                                            name="networkProvider"
                                            value={formData.networkProvider}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Network Provider...</option>
                                            <option value="1nceDirect">1NCE Direct</option>
                                            <option value="at&t">AT&T</option>
                                            <option value="Sprint">Sprint</option>
                                            <option value="Verizon">Verizon</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="button-group">
                                <button
                                    type="submit"
                                    className="submit-button"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting Data...' : 'Submit'}
                                </button>
                                {(String(formData.isTrip || '').trim().toLowerCase() === 'trip' || String(formData.isTrip || '').trim().toLowerCase() === 'customtrip') && (
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                            {submitMessage && <span className="submit-message">{submitMessage}</span>}
                        </form>
                    </div>
                </div>

                {/* Right Section - Wiki Info (25%) */}
                <div className="wiki-section">
                    <div className="wiki-panel">
                        <a href="https://wiki.spireon.com/pages/viewpage.action?spaceKey=STE&title=EventGenerationService" target="_blank" rel="noopener noreferrer">
                            EventGenerationService Reference
                        </a>
                        <h3>Serial Number Info</h3>
                        <p>XirgoEventPush----------------[399337801] [Serial Number]</p>
                        <p>queclinkEventPush-------------[337337800000016] [Serial Number]</p>
                        <p>onceEventPush-----------------[337337800000015] [Serial Number]</p>
                        <p>montageIonEventPush-----------[A2337337000001] [Serial Number]</p>
                        <p>montageArsenalEventPush-------[337337800000013] [Serial Number]</p>
                        <p>apolloUdpEventPush------------[337337800000014] [Serial Number]</p>
                        <p>FordDevice--------------------[337337100FORD75] [Serial Number]</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventGeneratorForm;
